import React from 'react';

import { AuthContextV1Raw } from '../../containers/login/view-model/contexts/auth-context/AuthContextV1';
import { CityContextRaw } from '../../contexts/city-context/CityContext';
import { ModalContextRaw } from '../../contexts/modal-context/ModalContext';
import { SeoWithSchema } from '../../shared/helpers/SeoMetaMapper';
import SEOCmp from '../../shared/seo/view/components/SEOCmp';

import { MainLayoutBindings } from './useMainLayout';

interface LayoutShellProps {
  metaData: SeoWithSchema;
  bindings: MainLayoutBindings;
  header: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Shared shell for MainLayout / MainLayoutV2: renders SEO, wires the auth /
 * city / modal context providers, and lays out header → children → footer →
 * modal inside the locale+auth keyed div. The variants differ only in the
 * `header` / `footer` slots they pass in.
 */
function LayoutShell({
  metaData,
  bindings,
  header,
  footer,
  children,
}: Readonly<LayoutShellProps>): React.ReactElement {
  const {
    authContextValue,
    cityContextValue,
    modalContextValue,
    modalComponent,
    layoutKey,
  } = bindings;

  return (
    <>
      <SEOCmp
        seo={{
          title: metaData.title,
          meta_description: metaData.description,
          local_data: metaData.schemas,
          image: metaData.image,
          is_no_index: metaData.is_no_index,
        }}
      />
      <AuthContextV1Raw.Provider value={authContextValue}>
        <CityContextRaw.Provider value={cityContextValue}>
          <ModalContextRaw.Provider value={modalContextValue}>
            <div key={layoutKey}>
              {header}
              {children}
              {footer}
              {modalComponent}
            </div>
          </ModalContextRaw.Provider>
        </CityContextRaw.Provider>
      </AuthContextV1Raw.Provider>
    </>
  );
}

export default LayoutShell;
